
import { Entry, UUID } from '@Protium/vue-ozone-utils'
import { set as _set, orderBy, reverse } from 'lodash'

export enum SortingDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export enum PadPosition {
  Pre = 'pre',
  Before = 'pre',
  Post = 'post',
  After = 'post'
}

export const uuid = (a = ''): UUID => (
  a

    ? ((Number(a) ^ Math.random() * 16) >> Number(a) / 4).toString(16)
    : (`${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`).replace(/[018]/g, uuid)
)

export function $get<T extends Array<any> | Record<string, any>, K extends keyof T = any> (obj: T, path: Array<string> | string, defaultValue?: any): T[K] {
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path || '', regexp)
      .filter(Boolean)
      .reduce((res, key) => (res !== null && res !== undefined ? res[key] : res), obj)
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/)
  return result === undefined || result === obj ? defaultValue : result
}

// NOTE that this can be worked into the $get function above, but for the sake of keeping that function as light as possible this is split off
// get values from a subpath for each item within the array as path
export function $getArray<T extends Record<string, any>> (obj: Array<T> | T, path: Array<string> | string, subpath: Array<string> | string, defaultValue: any = undefined): Array<any> {
  const a = $get(obj, path, [])
  return a.map(i => $get(i, subpath, defaultValue))
}

export function $castArray<T> (payload: T | Array<T>): Array<T> {
  return Array.isArray(payload) ? payload : [payload]
}

export function $groupBy<T extends Array<any> = any> (items: T, path: string | Array<string> | ((item: T) => any)) {
  return items.reduce((curr, entry): Record<string, Array<T>> => {
    const keys: Array<string> = $castArray(typeof path === 'function' ? path(entry) : $get(entry, path))
    keys.forEach(key => {
      if (curr[key]) curr[key].push(entry)
      else curr[key] = [entry]
    })

    return curr
  }, {})
}

export function $sortBy (items: Array<any>, path: string | Array<string>, direction: SortingDirection | Array<SortingDirection> = SortingDirection.ASC): Array<any> {
  return orderBy(items, path, Array.isArray(direction) ? direction : [direction as any || SortingDirection.ASC])
}

export function $set (target: Record<string, any>, path: string | Array<string>, value: any) {
  return _set(target, path, value)
}

export function $setTo<T extends Record<string, any>> (payload: Array<T>, addition: T | Array<T>, path = 'sys.id', prepend?: boolean) {
  const items = $castArray(addition).filter(Boolean)
  if (!Array.isArray(payload)) {
    console.warn('$setTo payload array was undefined/null or not an Array. defaulting to empty array', payload)
  }
  const mutated = payload || []

  items.forEach(item => {
    const i = mutated.findIndex(n => item && ($get(n, path) === $get(item, path)))
    // @Links will ignore a field update if it was an entry or array of entries, and is now a UUID
    let parsedItem: T = item

    if (i >= 0) {
      parsedItem = Object.fromEntries(
        Object.keys(item).map(key => {
          if (
            ((Array.isArray(mutated[i][key]) && mutated[i][key].every(isEntry)) && (Array.isArray(item[key]) && item[key].every(isUUID))) ||
            (mutated[i][key] && (isEntry(mutated[i][key]) && isUUID(item[key])))
          ) {
            return [key, mutated[i][key]]
          }
          return [key, item[key]]
        })
      ) as T
    }

    (i >= 0)
      ? mutated.splice(i, 1, parsedItem)
      : prepend ? mutated.unshift(item) : mutated.push(item)
  })

  return mutated
}

export function $padString (string: string, pad = '', length?: number, position = PadPosition.Pre): string {
  const padChars = [...Array(Math.max((string.length || 0), (length || 0)) - (string.length || (length || 0)))].map(() => pad).join('')
  return position === PadPosition.Pre ? `${padChars}${string}` : `${string}${padChars}`
}

export function $reverse<A extends Array<any>> (array: A) {
  const a: A = [] as unknown as A
  for (let i = array.length - 1; i >= 0; i--) a.push(array[i])
  return a
}

// ------

export const debounce = <T extends Array<any>> (
  callback: (..._: T) => any,
  wait: number
): (..._: T) => void => {
  const next = () => {
    timeout = clearTimeout(timeout as NodeJS.Timeout) as undefined
    callback(...lastArgs)
  }
  let timeout: NodeJS.Timeout | undefined
  let lastArgs: T

  return (...args: T) => {
    lastArgs = args

    if (timeout === void 0) {
      timeout = setTimeout(next, wait)
    }
  }
}

export const throttle = <T extends Array<any>> (callback: (..._: T) => void, wait: number): (..._: T) => void => {
  let queuedToRun: NodeJS.Timeout | undefined | number
  let previouslyRun: number
  return function invokeFn (...args: T | Array<any>) {
    const now = Date.now()
    queuedToRun = clearTimeout(queuedToRun as NodeJS.Timeout) as undefined
    if (!previouslyRun || (now - previouslyRun >= wait)) {
      callback(...args as T)
      previouslyRun = now
    } else {
      queuedToRun = setTimeout(invokeFn.bind(null, ...args), wait - (now - previouslyRun))
    }
  }
}

// ------ Entries

export function isReference (field: any): boolean {
  return (typeof field === 'string' && isUUID(field)) ||
    (Array.isArray(field) && field.every(f => typeof f === 'object' && !Array.isArray(f) && f.sys && isUUID(f.sys.id))) ||
    (Array.isArray(field) && field.every(f => typeof f === 'string' && isUUID(f))) ||
    (!!field && typeof field === 'object' && !!field.sys && isUUID(field.sys.id))
}

export function validateEntry (entry: Entry | UUID): boolean {
  return (typeof entry === 'string' && isUUID(entry)) || (isEntry(entry))
}

export function isEntry (e: Entry | UUID): e is Entry {
  return (e as Entry).sys !== undefined
}

// ------

// NOTE disabling eslint as it's purpose is to assert if ANY input is json
export function isJSON (input: any): boolean { // eslint-disable-line
  try {
    if (typeof input === 'string') {
      JSON.parse(input)
      return true
    }
    return (
      (Array.isArray(input) && typeof input[0] === 'object') ||
      (typeof input === 'object' && !Array.isArray(input))
    )
  } catch (e) {
    return false
  }
}

export function isUUID (uuid: string | any): boolean {
  if (typeof uuid !== 'string') return false // somehow this will return true if uuid is passed an array containing a uuid...
  const rx = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return rx.test(uuid)
}

const DATA_URI_IMAGE_MIME_TYPES = ['jpeg', 'png', 'gif', 'webp', 'svg+xml', 'bmp', 'avif']
const DATA_URI_IMAGE_RX = /^data:image\/([^;]+);base64,(.*)$/is
const DATA_URI_RX = /^data:([^;]+);base64,(.*)$/is

function isRenderableDataImageMime (mime: string): boolean {
  return DATA_URI_IMAGE_MIME_TYPES.includes(mime.toLowerCase() === 'jpg' ? 'jpeg' : mime.toLowerCase())
}

function decodeBase64Text (value: string): string | undefined {
  try {
    return typeof atob === 'function' ? atob(value) : undefined
  } catch (e) {
    return undefined
  }
}

export function isDataImageUri (value: any): value is string {
  if (typeof value !== 'string') return false
  const match = value.trim().match(DATA_URI_IMAGE_RX)
  return !!match && isRenderableDataImageMime(match[1])
}

export function isRenderableImageSource (value: any): value is string {
  return typeof value === 'string' && (/^https?:\/\//.test(value.trim()) || isDataImageUri(value))
}

export function normalizeImageSource<T> (value: T): T | string {
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  const dataUriMatch = trimmed.match(DATA_URI_RX)
  if (!dataUriMatch) return trimmed

  const [, mime, rawPayload] = dataUriMatch
  const payload = rawPayload.replace(/\s/g, '')

  if (!isDataImageUri(trimmed)) {
    const decodedPayload = decodeBase64Text(payload)?.trim()
    return decodedPayload?.startsWith('data:image/')
      ? normalizeImageSource(decodedPayload)
      : trimmed
  }

  const normalizedMime = mime.toLowerCase() === 'image/jpg' ? 'image/jpeg' : mime
  return `data:${normalizedMime};base64,${payload}`
}

export function checkByteSizeLimit (string: string): boolean {
  // console.log('checkByteSizeLimit', Buffer.byteLength(string, 'utf8'))
  return Buffer.byteLength(string, 'utf8') > 1024 * 2
}

// NOTE disabling eslint as it's purpose is to divide ANY array
// NOTE edited types to be compatible with any array and run without disabling eslint
export function divideArray (a: any[], s: number) {
  return Array(Math.ceil(a.length / s)).fill(0).map((_, i) => a.slice(i * s, i * s + s))
}

const S4 = () =>
  (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
export function guid (): string {
  return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`
}

// typesafe not-empty check for bitch-ass typescript
export function notEmpty<TValue> (value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

// ---

export function mode (array) {
  return [...array].sort((a, b) =>
    [...array].filter(v => v === a).length - [...array].filter(v => v === b).length).pop()
}

/**
 *
 * @param c numeric value
 * @param x the number that will be counted around from 0 to x
 * @returns c as a function of x, where the returned value is: >= 0 and <= x
 */
export function countAround (c: number, x: number) {
  return c - (x * Math.floor(c / x))
}
